/**
 * Image acceptance gate (build-plan §6.3 / §10.6; spec 0020 Slice 0). Client-side pre-check ONLY — the
 * real upload endpoint (`POST /api/assets/images`: auth + store-scope + rate-limit + server re-sniff +
 * EXIF strip + WebP transcode — ANIMATED WebP for a multi-frame GIF/WebP, so animations keep playing —
 * + opaque server key) is the authoritative gate. Here we validate MIME +
 * size + magic-byte sniff (so a `.html` renamed `.png` is rejected cheaply, before the network round-
 * trip); the stored dimensions come back from the endpoint (the transcoded WebP's real dims).
 *
 * Slice 0 replaced the old data-URL stub: the control now uploads the file (see `uploadAsset.ts`) and
 * stores the returned delivery URL in `ImageValue.url` — no more base64 inline in the JSONB document.
 */
import { bt } from '../../i18n';

export const ACCEPTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  // AVIF + HEIC/HEIF: decoded server-side by libvips (backend 0c2d3b3), stored as WebP like the rest.
  'image/avif',
  'image/heic',
  'image/heif',
] as const;
/** The `accept` attribute for the hidden file input — MIME list + extensions (Chrome reports an EMPTY MIME for HEIC). */
export const ACCEPT_ATTR = `${ACCEPTED_IMAGE_TYPES.join(',')},.avif,.heic,.heif`;
const EXT_TO_TYPE: Record<string, string> = {
  avif: 'image/avif',
  heic: 'image/heic',
  heif: 'image/heif',
};

/** The file's MIME, falling back to its extension when the browser reports none (Chrome does for HEIC). */
function effectiveType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  return EXT_TO_TYPE[ext] ?? '';
}
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024; // 15 MB UX cap — mirrors ImageUploadService.MAX_BYTES

/** Sniff the leading magic bytes to confirm the file really is the image type it claims. */
async function sniffMagicBytes(file: File): Promise<boolean> {
  const buf = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const isPng = buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47;
  const isJpeg = buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff;
  const isGif = buf[0] === 0x47 && buf[1] === 0x49 && buf[2] === 0x46;
  // WEBP: "RIFF"...."WEBP"
  const isWebp =
    buf[0] === 0x52 &&
    buf[1] === 0x49 &&
    buf[2] === 0x46 &&
    buf[3] === 0x46 &&
    buf[8] === 0x57 &&
    buf[9] === 0x45 &&
    buf[10] === 0x42 &&
    buf[11] === 0x50;
  // AVIF / HEIC / HEIF: ISO-BMFF — "ftyp" at byte 4, then a brand among avif/avis/heic/heix/mif1/msf1
  // (the same brand list the backend's ImageType.sniff accepts).
  const ascii = (from: number, to: number) => String.fromCharCode(...buf.subarray(from, to));
  const isBmff =
    ascii(4, 8) === 'ftyp' &&
    ['avif', 'avis', 'heic', 'heix', 'mif1', 'msf1'].includes(ascii(8, 12));
  return isPng || isJpeg || isGif || isWebp || isBmff;
}

/** Validate MIME + size + magic bytes. Throws a user-facing message on rejection; returns on success. */
export async function validateImageFile(file: File): Promise<void> {
  if (!(ACCEPTED_IMAGE_TYPES as readonly string[]).includes(effectiveType(file))) {
    throw new Error(
      bt(
        'chrome.media.image_type',
        'Unsupported image type. Use PNG, JPEG, WebP, GIF, AVIF or HEIC.',
      ),
    );
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error(bt('chrome.media.image_too_large', 'That image is too large (max 15 MB).'));
  }
  if (!(await sniffMagicBytes(file))) {
    throw new Error(
      bt('chrome.media.image_not_image', "That file doesn't look like a real image."),
    );
  }
}

/** Pull the first image File from a paste/drop event's items, or null. */
export function imageFromTransfer(items: DataTransferItemList | null): File | null {
  if (!items) return null;
  for (const item of Array.from(items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const f = item.getAsFile();
      if (f) return f;
    }
  }
  return null;
}
